import React, { Fragment, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from 'model/Api';
import { User } from 'model/User';
import { Role, useRole } from 'model/Role';
import { generalStyle } from 'helper/Style';
import Layout from 'component/part/Layout';
import BackLink from 'component/part/BackLink';
import FormContent from 'component/part/FormContent';
import OperationTrigger from 'component/part/OperationTrigger';
import Select from 'component/part/Select';
import H2 from 'component/part/H2';
import RadioList from 'component/part/RadioList';

const initialUser: User = {
  id: 0,
  lastName: '',
  firstName: '',
  roleId: 0,
  nameNotationType: 'space',
};

type NameNotationType = {
  value: User['nameNotationType'];
  label: string;
};

const needSalary = (user: User) =>
  user.role?.permissions.workAssign ||
  user.role?.permissions.workSupervise;

const style = {
  form: {
    ...generalStyle.form,
    ['& input']: {
      maxWidth: '32rem',
    },
  },
  nameArea: {
    ...generalStyle.formRow,
    alignItems: 'start',
    gap: '4rem',
    ['& > div:nth-of-type(1) input']: {
      width: '30rem',
    },
  },
  emailArea: {
    ...generalStyle.formRow,
    gap: '0.5rem',
    ['& input']: {
      width: '20rem !important',
    },
  },
};

const MemberEdit: React.FC = () => {
  const api = useApi();
  const { getListAsync: getRoleListAsync } = useRole();
  const navigate = useNavigate();
  const urlParams = useParams<{ id: string }>();
  const userId = parseInt(urlParams.id || '');
  const [user, setUser] = useState<User>(initialUser);
  const [userRole, setUserRole] = useState<Role>();
  const [roles, setRoles] = useState<Role[]>([]);
  const [originalUserRole, setOriginalUserRole] = useState<User['role']>();
  const [emailAccount, setEmailAccount] = useState('');
  const [emailDomain, setEmailDomain] = useState('');
  const [editingPassword, setEditingPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordRepetition, setPasswordRepetition] = useState('');
  const lastName = user.lastName || 'カイタイ';
  const firstName = user.firstName || 'タロウ';
  const nameNotationTypes: NameNotationType[] = [
    { value: 'space', label: `'${lastName} ${firstName}'` },
    { value: 'point', label: `'${lastName}・${firstName}'` },
    { value: 'western', label: `'${firstName}・${lastName}'` },
  ];
  const nameNotationType = nameNotationTypes.find(({ value }) => value === (user.nameNotationType || 'space'));

  const onChangeUser = <T extends keyof User>(key: T, value: User[T]) => {
    setUser({ ...user, [key]: value });
  };

  const onSubmit = async () => {
    if (userRole) {
      user.roleId = userRole.id;
    }
    const option: Partial<{ password: string }> = {};
    if (userRole?.permissions.login) {
      user.email = `${emailAccount}@${emailDomain}`;
      if (editingPassword) {
        option.password = password;
      }
    }

    const result = await api.user.update(user, option);

    return result.ok && !!result.data;
  };

  useEffect(() => {
    (async () => {
      if (userId) {
        const userResponse = await api.user.get(userId, { with: ['role'] });
        if (userResponse) {
          if (userResponse.role?.permissions.login) {
            const emailParts = (userResponse.email || '').match(/(.*)@(.*)/);
            setEmailAccount(emailParts?.[1] || '');
            setEmailDomain(emailParts?.[2] || '');
          }
          setUser(userResponse);
          setEditingPassword(!userResponse.role?.permissions.login);
          setUserRole(userResponse.role);
          setOriginalUserRole(userResponse.role);
        } else {
          setUser({ ...user, id: userId });
        }
      } else {
        setEditingPassword(true);
      }
    })();
  }, [userId]);

  useEffect(() => {
    (async () => {
      setRoles(await getRoleListAsync());
    })();
  }, []);

  return <Layout>
    <BackLink link='/member/list' label='メンバー一覧'></BackLink>
    <H2>{userId ? 'メンバー編集' : 'メンバー登録'}</H2>
    <div css={style.form}>
      <div css={style.nameArea}>
        <div css={generalStyle.form}>
          <FormContent label='姓'>
            <input type='text' value={user.lastName} onChange={(e) => onChangeUser('lastName', e.target.value)} />
          </FormContent>
          <FormContent label='名'>
            <input type='text' value={user.firstName} onChange={(e) => onChangeUser('firstName', e.target.value)} />
          </FormContent>
        </div>
        <FormContent label='氏名表記'>
          <RadioList
            list={nameNotationTypes}
            value={nameNotationType}
            labelKey='label'
            onChange={(item) => onChangeUser('nameNotationType', item.value)}
            direction='column'
          />
        </FormContent>
      </div>
      <FormContent label='役職'>
        <Select
          list={roles}
          value={roles.find(({ id }) => id === userRole?.id)}
          labelKey='name'
          onChange={(item) => item && setUserRole(item)}
        />
      </FormContent>
      {userRole?.permissions.login && (
        <Fragment>
          <FormContent label='ユーザー名'>
            <input type='text' value={user.accountName} onChange={(e) => onChangeUser('accountName', e.target.value)} />
          </FormContent>
          <FormContent label='メールアドレス'>
            <div css={style.emailArea}>
              <input type='text' value={emailAccount} onChange={(e) => setEmailAccount(e.target.value)} />
              <span>@</span>
              <input type='text' value={emailDomain} onChange={(e) => setEmailDomain(e.target.value)} />
            </div>
          </FormContent>
          {originalUserRole?.permissions.login && (
            <label>
              <input type='checkbox' onChange={() => setEditingPassword(!editingPassword)} checked={editingPassword} />
              パスワードを再設定する
            </label>
          )}
          {editingPassword && (
            <Fragment>
              <FormContent label='パスワード'>
                <input type='password' value={password} onChange={(e) => setPassword(e.target.value)} />
              </FormContent>
              <FormContent label='パスワード（再入力）'>
                <input type='password' value={passwordRepetition} onChange={(e) => setPasswordRepetition(e.target.value)} />
              </FormContent>
            </Fragment>
          )}
        </Fragment>
      )}
      {needSalary(user) && (
        <Fragment>
          <FormContent label='日給'>
            <input type='number' value={user.salary} onChange={(e) => onChangeUser('salary',  parseInt(e.target.value))} />
          </FormContent>
        </Fragment>
      )}
      <OperationTrigger
        onSubmit={onSubmit}
        onOk={() => navigate('/member/list')}
        buttonText={userId ? '更新' : '登録'}
        objectType='member'
        operationType={userId ? 'edit' : 'create'}
      />
    </div>
  </Layout>;
};

export default MemberEdit;
